import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button, Chip } from "@heroui/react";
import { CheckIcon } from "@heroicons/react/24/outline";
import SignatureCanvas from "react-signature-canvas";

interface SignaturePadProps {
  signature: string | null;
  onSignatureChange: (dataUrl: string | null) => void;
}

const CANVAS_HEIGHT = 144;

const SignaturePad: React.FC<SignaturePadProps> = ({
  signature,
  onSignatureChange,
}) => {
  const { t } = useTranslation("dealModal");
  const padRef = useRef<SignatureCanvas>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const canvas = padRef.current?.getCanvas();
    if (canvas && wrapRef.current) {
      canvas.width = wrapRef.current.clientWidth;
      canvas.height = CANVAS_HEIGHT;
    }
  }, []);

  const handleEnd = () => {
    if (!padRef.current || padRef.current.isEmpty()) return;
    onSignatureChange(padRef.current.toDataURL("image/png"));
  };

  const handleClear = () => {
    padRef.current?.clear();
    onSignatureChange(null);
  };

  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-[#11181C]">{t("yourSignature")}</span>
          {signature && (
            <Chip size="sm" color="success" variant="flat"
              startContent={<CheckIcon className="size-3" strokeWidth={3} />}>
              {t("signed")}
            </Chip>
          )}
        </div>
        <Button size="sm" variant="light" radius="full" onPress={handleClear} isDisabled={!signature}>
          {t("clear")}
        </Button>
      </div>

      <div
        ref={wrapRef}
        className={`
          overflow-hidden rounded-2xl border bg-white
          ${signature ? "border-[#16A34A]" : "border-dashed border-[#A1A1AA]"}
        `}
      >
        <SignatureCanvas
          ref={padRef}
          onEnd={handleEnd}
          penColor="#11181C"
          canvasProps={{ className: "block touch-none", style: { height: CANVAS_HEIGHT } }}
        />
      </div>
      {!signature && (
        <p className="mt-1 text-xs text-[#71717A]">{t("signInsideBox")}</p>
      )}
    </div>
  );
};

export default SignaturePad;
